<script lang="ts" setup>
import { ref, onMounted, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'
import { useAxios } from '@/composables/useAxios'
import { handleAxiosError } from '@/composables/useAxiosError'
import { useNotifyStore } from '@/stores'
import { uuid } from '@/composables'
import { STATUS_CODES } from '@/constants'
import { DateTime } from 'luxon'
import type { PendingVerificationHandle } from '@/types/compliance'
import BaseDashboardPage from '@/components/base/BaseDashboardPage.vue'

defineOptions({ name: 'HandleRevalidationList' })

const { t } = useI18n()
const route = useRoute()
const notifyStore = useNotifyStore()

const loading = ref(true)
const handles = ref<PendingVerificationHandle[]>([])
const statusFilter = ref<'pending' | 'expired'>((route.query.status as 'pending' | 'expired') || 'pending')
const searchQuery = ref('')
const revalidatingId = ref<number | null>(null)

// Fetch handles needing verification
async function fetchHandles() {
  loading.value = true

  try {
    const res = await useAxios<null, PendingVerificationHandle[]>(
      'GET',
      `/admin/handles/pending-verification?status=${statusFilter.value}`,
      true
    )

    if (res && res.status === STATUS_CODES.OK && res.data) {
      handles.value = res.data
    }
  } catch (err) {
    handleAxiosError(err as Error, { logPrefix: 'HandleRevalidationList' })
  } finally {
    loading.value = false
  }
}

// Re-fetch when filter changes
watch(statusFilter, () => {
  fetchHandles()
})

// Client-side search filter
const filteredHandles = ref<PendingVerificationHandle[]>([])
watch([handles, searchQuery], () => {
  if (!searchQuery.value.trim()) {
    filteredHandles.value = handles.value
    return
  }
  const query = searchQuery.value.toLowerCase()
  filteredHandles.value = handles.value.filter(h =>
    h.account_email.toLowerCase().includes(query) ||
    h.external_name?.toLowerCase().includes(query)
  )
}, { immediate: true })

// Format date
function formatDate(dateString: string): string {
  return DateTime.fromISO(dateString).toFormat('dd.MM.yyyy')
}

// Calculate days remaining from deadline
function getDaysRemaining(deadline: string): number {
  const now = DateTime.now()
  const deadlineDate = DateTime.fromISO(deadline)
  return Math.ceil(deadlineDate.diff(now, 'days').days)
}

// Get deadline status
function getDeadlineStatus(handle: PendingVerificationHandle): { class: string; text: string } {
  const daysRemaining = getDaysRemaining(handle.verification_deadline)

  if (daysRemaining < 0) {
    return { class: 'expired', text: t('dashboard.admin.compliance.handles.expired') }
  }
  if (daysRemaining <= 7) {
    return { class: 'critical', text: `${daysRemaining} ${t('dashboard.admin.compliance.handles.days')}` }
  }
  if (daysRemaining <= 14) {
    return { class: 'warning', text: `${daysRemaining} ${t('dashboard.admin.compliance.handles.days')}` }
  }
  return { class: 'normal', text: `${daysRemaining} ${t('dashboard.admin.compliance.handles.days')}` }
}

// Get handle type icon
function getTypeIcon(type: 'person' | 'org' | 'role'): string {
  switch (type) {
    case 'person': return 'mdi-account'
    case 'org': return 'mdi-domain'
    case 'role': return 'mdi-account-tie'
  }
}

// Trigger revalidation
async function triggerRevalidation(handle: PendingVerificationHandle) {
  revalidatingId.value = handle.id

  try {
    const res = await useAxios<null, null>(
      'POST',
      `/admin/handle/${handle.id}/revalidate`,
      true
    )

    if (res && res.status === STATUS_CODES.OK) {
      notifyStore.createNotify({
        id: uuid('v4'),
        alertType: 'success',
        title: t('dashboard.admin.compliance.handles.revalidateSuccess'),
        message: handle.account_email
      })
      await fetchHandles()
    }
  } catch (err) {
    handleAxiosError(err as Error, { logPrefix: 'HandleRevalidationList:revalidate' })
    notifyStore.createNotify({
      id: uuid('v4'),
      alertType: 'error',
      title: t('dashboard.admin.compliance.handles.revalidateError'),
      message: handle.account_email
    })
  } finally {
    revalidatingId.value = null
  }
}

onMounted(() => {
  fetchHandles()
})
</script>

<template>
  <BaseDashboardPage
    :title="t('dashboard.admin.compliance.handles.title')"
    :subtitle="t('dashboard.admin.compliance.handles.subtitle')"
    icon="mdi-account-clock-outline"
    icon-color="blue"
    show-back
    :back-route="{ name: 'dashboardAdminCompliance' }"
  >
    <template #actions>
      <v-btn
        icon
        variant="text"
        :loading="loading"
        @click="fetchHandles"
      >
        <v-icon icon="mdi-refresh" />
      </v-btn>
    </template>

    <!-- Filters -->
    <div class="filters-bar">
      <v-text-field
        v-model="searchQuery"
        prepend-inner-icon="mdi-magnify"
        :placeholder="t('dashboard.admin.compliance.handles.searchPlaceholder')"
        variant="outlined"
        density="compact"
        hide-details
        class="search-input"
      />
      <v-btn-toggle
        v-model="statusFilter"
        mandatory
        density="compact"
        class="status-toggle"
      >
        <v-btn value="pending" variant="outlined" size="small">
          {{ t('dashboard.admin.compliance.handles.filterPending') }}
        </v-btn>
        <v-btn value="expired" variant="outlined" size="small">
          {{ t('dashboard.admin.compliance.handles.filterExpired') }}
        </v-btn>
      </v-btn-toggle>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="loading-state">
      <v-progress-circular indeterminate size="48" color="primary" />
    </div>

    <!-- Empty State -->
    <div v-else-if="filteredHandles.length === 0" class="empty-state">
      <v-icon icon="mdi-check-circle-outline" size="64" />
      <h3>{{ t('dashboard.admin.compliance.handles.empty') }}</h3>
      <p>{{ t('dashboard.admin.compliance.handles.emptyDescription') }}</p>
    </div>

    <!-- Handles Table -->
    <div v-else class="table-container">
      <table class="handles-table">
        <thead>
          <tr>
            <th>{{ t('dashboard.admin.compliance.handles.columns.handle') }}</th>
            <th>{{ t('dashboard.admin.compliance.handles.columns.type') }}</th>
            <th>{{ t('dashboard.admin.compliance.handles.columns.email') }}</th>
            <th>{{ t('dashboard.admin.compliance.handles.columns.phone') }}</th>
            <th>{{ t('dashboard.admin.compliance.handles.columns.deadline') }}</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="handle in filteredHandles"
            :key="handle.id"
            class="table-row"
            :class="{ 'is-expired': statusFilter === 'expired' }"
          >
            <td class="handle-cell">
              <span class="handle-name">{{ handle.external_name || '-' }}</span>
              <router-link
                :to="{ name: 'dashboardAdminUsers', query: { search: handle.account_email } }"
                class="handle-email account-link"
                @click.stop
              >
                {{ handle.account_email }}
              </router-link>
            </td>
            <td class="type-cell">
              <v-icon :icon="getTypeIcon(handle.type)" size="18" />
            </td>
            <td class="verification-cell">
              <span class="verification-badge" :class="handle.email_verified ? 'verified' : 'missing'">
                <v-icon :icon="handle.email_verified ? 'mdi-check' : 'mdi-close'" size="14" />
              </span>
            </td>
            <td class="verification-cell">
              <span class="verification-badge" :class="handle.phone_verified ? 'verified' : 'missing'">
                <v-icon :icon="handle.phone_verified ? 'mdi-check' : 'mdi-close'" size="14" />
              </span>
            </td>
            <td class="deadline-cell">
              <span class="deadline-badge" :class="getDeadlineStatus(handle).class">
                {{ getDeadlineStatus(handle).text }}
              </span>
              <span class="deadline-date">
                {{ formatDate(handle.verification_deadline) }}
              </span>
            </td>
            <td class="action-cell">
              <v-btn
                color="primary"
                variant="tonal"
                size="small"
                :loading="revalidatingId === handle.id"
                @click="triggerRevalidation(handle)"
              >
                <v-icon icon="mdi-email-sync" class="me-1" size="16" />
                {{ t('dashboard.admin.compliance.handles.revalidate') }}
              </v-btn>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </BaseDashboardPage>
</template>

<style lang="scss" scoped>
/* Filters */
.filters-bar {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 24px;
  flex-wrap: wrap;
}

.search-input {
  max-width: 300px;
  flex: 1;
}

.status-toggle {
  flex-wrap: wrap;
}

/* Loading/Empty States */
.loading-state,
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 24px;
  text-align: center;
}

.empty-state {
  .v-icon {
    color: rgba(76, 175, 80, 0.4);
    margin-bottom: 16px;
  }

  h3 {
    font-size: 1.1rem;
    font-weight: 600;
    color: rgba(255, 255, 255, 0.8);
    margin-bottom: 8px;
  }

  p {
    font-size: 0.9rem;
    color: rgba(200, 218, 255, 0.5);
    margin: 0;
  }
}

/* Table */
.table-container {
  background: rgba(15, 23, 48, 0.5);
  border: 1px solid rgba(120, 163, 255, 0.1);
  border-radius: 12px;
  overflow: hidden;
}

.handles-table {
  width: 100%;
  border-collapse: collapse;

  th, td {
    padding: 14px 16px;
    text-align: left;
  }

  th {
    font-size: 0.75rem;
    font-weight: 600;
    color: rgba(200, 218, 255, 0.5);
    text-transform: uppercase;
    letter-spacing: 0.05em;
    background: rgba(0, 0, 0, 0.15);
    border-bottom: 1px solid rgba(120, 163, 255, 0.1);
  }
}

.table-row {
  border-bottom: 1px solid rgba(120, 163, 255, 0.05);

  &:last-child {
    border-bottom: none;
  }

  &.is-expired {
    background: rgba(239, 83, 80, 0.05);
  }
}

.handle-cell {
  .handle-name {
    display: block;
    font-weight: 500;
    color: rgba(255, 255, 255, 0.9);
  }

  .handle-email {
    display: block;
    font-size: 0.8rem;
    color: rgba(200, 218, 255, 0.5);
  }

  .account-link {
    text-decoration: none;
    transition: color 0.2s ease;

    &:hover {
      color: #2196f3;
      text-decoration: underline;
    }
  }
}

.type-cell {
  color: rgba(200, 218, 255, 0.6);
}

.verification-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 6px;

  &.verified {
    background: rgba(76, 175, 80, 0.15);
    color: #4caf50;
  }

  &.missing {
    background: rgba(239, 83, 80, 0.15);
    color: #ef5350;
  }
}

.deadline-cell {
  .deadline-badge {
    display: inline-flex;
    padding: 4px 10px;
    border-radius: 6px;
    font-size: 0.8rem;
    font-weight: 600;

    &.normal {
      background: rgba(76, 175, 80, 0.15);
      color: #81c784;
    }

    &.warning {
      background: rgba(255, 193, 7, 0.15);
      color: #ffc107;
    }

    &.critical {
      background: rgba(255, 152, 0, 0.15);
      color: #ffb74d;
    }

    &.expired {
      background: rgba(239, 83, 80, 0.15);
      color: #ef5350;
    }
  }

  .deadline-date {
    display: block;
    margin-top: 4px;
    font-size: 0.75rem;
    color: rgba(200, 218, 255, 0.4);
  }
}

.action-cell {
  text-align: right;
}

/* Light Theme */
.v-theme--lightTheme {
  .table-container {
    background: rgba(255, 255, 255, 0.8);
    border-color: rgba(28, 110, 227, 0.1);
  }

  .handles-table th {
    background: rgba(0, 0, 0, 0.02);
    color: rgba(15, 23, 42, 0.5);
  }

  .table-row.is-expired {
    background: rgba(239, 83, 80, 0.03);
  }

  .handle-cell .handle-name {
    color: rgba(15, 23, 42, 0.9);
  }

  .handle-cell .handle-email {
    color: rgba(15, 23, 42, 0.5);
  }

  .type-cell {
    color: rgba(15, 23, 42, 0.6);
  }
}

/* Mobile */
@media (max-width: 768px) {
  .handle-revalidation-list {
    padding: 16px;
  }

  .filters-bar {
    flex-direction: column;
    align-items: stretch;
  }

  .search-input {
    max-width: 100%;
  }

  .table-container {
    overflow-x: auto;
  }

  .handles-table {
    min-width: 700px;
  }
}
</style>
